import { AnimatePresence, motion } from "framer-motion";
import { useEffect, useState } from "react";

interface GiftBoxRevealProps {
  active: boolean;
  onToyRevealed: () => void;
}

const GiftBoxReveal = ({ active, onToyRevealed }: GiftBoxRevealProps) => {
  const [stage, setStage] = useState<"shake" | "glow" | "open" | "show">("shake");

  useEffect(() => {
    if (!active) {
      setStage("shake");
      return;
    }
    setStage("shake");
    const t1 = setTimeout(() => setStage("glow"), 1200);
    const t2 = setTimeout(() => setStage("open"), 2000);
    const t3 = setTimeout(() => setStage("show"), 2800);
    const t4 = setTimeout(() => onToyRevealed(), 4000);
    return () => {
      clearTimeout(t1);
      clearTimeout(t2);
      clearTimeout(t3);
      clearTimeout(t4);
    };
  }, [active, onToyRevealed]);

  if (!active) return null;

  return (
    <div className="relative flex h-48 flex-col items-center justify-center gap-4">
      {(stage === "open" || stage === "show") &&
        Array.from({ length: 12 }).map((_, i) => (
          <motion.span
            key={i}
            className="pointer-events-none absolute text-xl"
            initial={{ opacity: 0, scale: 0, x: 0, y: 0 }}
            animate={{
              opacity: [0, 1, 0],
              scale: [0, 1.5, 0],
              x: Math.cos((i * Math.PI * 2) / 12) * 80,
              y: Math.sin((i * Math.PI * 2) / 12) * 80,
            }}
            transition={{ duration: 0.8, delay: i * 0.04 }}
          >
            {i % 3 === 0 ? "✨" : i % 3 === 1 ? "⭐" : "💫"}
          </motion.span>
        ))}

      <AnimatePresence mode="wait">
        {stage === "shake" && (
          <motion.div
            key="shake"
            className="select-none text-8xl"
            animate={{
              rotate: [0, -8, 8, -8, 8, -3, 3, 0],
              y: [0, -4, 0, -4, 0],
            }}
            transition={{ duration: 0.6, repeat: Infinity, repeatDelay: 0.2 }}
            exit={{ scale: 1.2, opacity: 0, transition: { duration: 0.3 } }}
          >
            🎁
          </motion.div>
        )}

        {stage === "glow" && (
          <motion.div
            key="glow"
            className="relative select-none text-8xl"
            initial={{ scale: 1.2 }}
            animate={{ scale: [1.2, 1.3, 1.2] }}
            transition={{ duration: 0.5, repeat: Infinity }}
            exit={{ scale: 2, opacity: 0, rotate: 20, transition: { duration: 0.4 } }}
          >
            🎁
            <motion.div
              className="absolute inset-0 -z-10 rounded-full blur-xl"
              style={{
                background:
                  "radial-gradient(circle, rgba(251,191,36,0.65) 0%, transparent 70%)",
              }}
              animate={{ opacity: [0.4, 0.8, 0.4], scale: [1, 1.4, 1] }}
              transition={{ duration: 0.6, repeat: Infinity }}
            />
          </motion.div>
        )}

        {stage === "open" && (
          <motion.div key="open" className="relative">
            <motion.div
              className="absolute left-1/2 -top-8 -translate-x-1/2 text-5xl"
              initial={{ y: 0, rotate: 0 }}
              animate={{ y: -80, rotate: 45, opacity: 0 }}
              transition={{ duration: 0.6, ease: "easeOut" }}
            >
              🎀
            </motion.div>
            <motion.div
              className="text-6xl"
              initial={{ scale: 1 }}
              animate={{ scale: [1, 0.9, 0.7], opacity: [1, 0.8, 0] }}
              transition={{ duration: 0.8 }}
            >
              📦
            </motion.div>
            <motion.div
              className="absolute left-1/2 top-0 -translate-x-1/2 text-5xl"
              initial={{ y: 20, scale: 0, opacity: 0 }}
              animate={{ y: -20, scale: [0, 1.4, 1.1], opacity: 1 }}
              transition={{ delay: 0.3, duration: 0.6, type: "spring", bounce: 0.6 }}
            >
              ⚽
            </motion.div>
          </motion.div>
        )}

        {stage === "show" && (
          <motion.div
            key="show"
            className="flex flex-col items-center gap-3"
            initial={{ scale: 0.8, opacity: 0 }}
            animate={{ scale: 1, opacity: 1 }}
          >
            <motion.div
              className="text-6xl"
              animate={{ y: [0, -8, 0], rotate: [0, -5, 5, 0] }}
              transition={{ duration: 1.5, repeat: Infinity, ease: "easeInOut" }}
            >
              ⚽
            </motion.div>
            <motion.p
              className="text-lg font-bold text-emerald-600"
              initial={{ opacity: 0, y: 10 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.3 }}
            >
              You won a Beach Ball!
            </motion.p>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
};

export default GiftBoxReveal;